import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import './TaskModal.css';

const TaskModal = ({ isOpen, onClose, onSave, habits, editingTask, onSaveHabit }) => {
    const [title, setTitle] = useState('');
    const [selectedHabitId, setSelectedHabitId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [priority, setPriority] = useState('medium');

    const [showHabitCreate, setShowHabitCreate] = useState(false);
    const [newHabitName, setNewHabitName] = useState('');
    const [newHabitColor, setNewHabitColor] = useState('#22c55e');

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({ openOnClick: false }),
        ],
        content: '',
    });

    useEffect(() => {
        if (isOpen) {
            if (editingTask) {
                setTitle(editingTask.title || '');
                setSelectedHabitId(editingTask.habitId || '');
                setStartDate(editingTask.startDate || '');
                setEndDate(editingTask.endDate || '');
                setStartTime(editingTask.startTime || '');
                setEndTime(editingTask.endTime || '');
                setPriority(editingTask.priority || 'medium');
                editor?.commands.setContent(editingTask.description || '');
            } else {
                setTitle('');
                setSelectedHabitId('');
                setStartDate(new Date().toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
                setStartTime('18:00');
                setEndTime('19:00');
                setPriority('medium');
                editor?.commands.setContent('');
            }
        }
    }, [isOpen, editingTask, editor]);

    if (!isOpen) return null;

    const handleSave = () => {
        if (!title.trim()) {
            alert('Title is required');
            return;
        }
        onSave({
            title,
            description: editor?.getHTML(),
            habitId: selectedHabitId,
            startDate,
            endDate,
            startTime,
            endTime,
            priority,
        });
    };

    const handleCreateHabitInTask = async () => {
        if (!newHabitName.trim()) return;
        const res = await onSaveHabit({
            name: newHabitName,
            color: newHabitColor,
            active: true
        });
        if (res && res._id) {
            setSelectedHabitId(String(res._id));
            setShowHabitCreate(false);
            setNewHabitName('');
        }
    };

    return (
        <div className="task-modal-overlay" onClick={onClose} id="task-modal-overlay">
            <div className="task-modal" onClick={(e) => e.stopPropagation()}>
                <div className="task-modal-header">
                    <button className="task-modal-close" onClick={onClose} aria-label="Close">
                        <i className="fi fi-rr-cross-small"></i>
                    </button>
                </div>
                
                <div className="task-modal-body">
                    <input 
                        type="text" 
                        className="task-title-input" 
                        placeholder="Add title" 
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        autoFocus
                    />

                    <div className="task-type-selector">
                        <button className="type-btn active">Task</button>
                    </div>

                    <div className="task-field-row">
                        <i className="fi fi-rr-clock"></i>
                        <div className="task-date-time-rows">
                            <div className="task-date-time-line">
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                            </div>
                            <div className="task-date-time-line">
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div className="task-field-row">
                        <i className="fi fi-rr-notebook"></i>
                        <select 
                            className="task-habit-select" 
                            value={selectedHabitId} 
                            onChange={(e) => {
                                if (e.target.value === 'CREATE_NEW') {
                                    setShowHabitCreate(true);
                                } else {
                                    setSelectedHabitId(e.target.value);
                                }
                            }}
                        >
                            <option value="">No Habit (Inbox)</option>
                            {habits.map(h => (
                                <option key={h._id} value={h._id}>{h.name}</option>
                            ))}
                            <hr />
                            <option value="CREATE_NEW">+ Create new habit...</option>
                        </select>
                    </div>

                    <div className="task-field-row">
                        <i className="fi fi-rr-align-left" style={{ marginTop: '12px' }}></i>
                        <div className="task-rich-editor-container">
                            <div className="task-tiptap-toolbar">
                                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor?.isActive('bold') ? 'is-active' : ''} title="Bold"><i className="fi fi-rr-bold"></i></button>
                                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor?.isActive('italic') ? 'is-active' : ''} title="Italic"><i className="fi fi-rr-italic"></i></button>
                                <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor?.isActive('underline') ? 'is-active' : ''} title="Underline"><i className="fi fi-rr-underline"></i></button>
                                <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor?.isActive('bulletList') ? 'is-active' : ''} title="Bullet List"><i className="fi fi-rr-list"></i></button>
                            </div>
                            <EditorContent editor={editor} className="task-description-editor" />
                        </div>
                    </div>

                    <div className="task-field-row">
                        <i className="fi fi-rr-flag"></i>
                        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="task-priority-select">
                            <option value="low">Low Priority</option>
                            <option value="medium">Medium Priority</option>
                            <option value="high">High Priority</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>
                </div>

                <div className="task-modal-footer">
                    <button className="cancel-task-btn" onClick={onClose}>Cancel</button>
                    <button className="save-btn" onClick={handleSave}>Save</button>
                </div>

                {showHabitCreate && (
                    <div className="habit-nested-modal-overlay">
                        <div className="habit-nested-modal">
                            <h3>Create New Habit</h3>
                            <div className="habit-nested-field">
                                <label>Habit Name</label>
                                <input 
                                    type="text" 
                                    value={newHabitName} 
                                    onChange={(e) => setNewHabitName(e.target.value)} 
                                    placeholder="e.g. Drink Water"
                                />
                            </div>
                            <div className="habit-nested-field-row">
                                <label>Color</label>
                                <input type="color" value={newHabitColor} onChange={(e) => setNewHabitColor(e.target.value)} />
                            </div>
                            <div className="habit-nested-actions">
                                <button onClick={() => setShowHabitCreate(false)}>Cancel</button>
                                <button className="save-habit-btn" onClick={handleCreateHabitInTask}>Save Habit</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskModal;
